/**
 * Pure helpers for the collapsible / resizable admin left nav (AdminLayout).
 *
 * The sidebar has two states: expanded (width in [NAV_MIN_WIDTH, NAV_MAX_WIDTH])
 * and collapsed (a slim NAV_COLLAPSED_WIDTH icon rail). Both are persisted in
 * localStorage under NAV_STORAGE_KEY; every read/write is try/catch-guarded by
 * the caller-facing helpers so the page renders fine without storage.
 */

export const NAV_COLLAPSED_WIDTH = 60;
export const NAV_DEFAULT_WIDTH = 256;
export const NAV_MIN_WIDTH = 180;
export const NAV_MAX_WIDTH = 360;
/** Dragging the handle to a width below this snaps the nav collapsed. */
export const NAV_SNAP_COLLAPSE_WIDTH = 120;
export const NAV_STORAGE_KEY = 'skillsnap.admin.nav';

export interface NavState {
  collapsed: boolean;
  width: number;
}

export const DEFAULT_NAV_STATE: NavState = { collapsed: false, width: NAV_DEFAULT_WIDTH };

/** Clamp an expanded width to [min, max]; non-finite input → default width. */
export function clampNavWidth(width: number): number {
  if (!Number.isFinite(width)) return NAV_DEFAULT_WIDTH;
  return Math.round(Math.min(NAV_MAX_WIDTH, Math.max(NAV_MIN_WIDTH, width)));
}

/**
 * Resolve a raw drag width (pointer x) into the resulting nav state.
 * Below the snap threshold → collapsed (the last expanded width is kept so
 * re-expanding restores it); otherwise expanded at the clamped width.
 */
export function resolveDragWidth(rawWidth: number, prevWidth: number): NavState {
  if (!Number.isFinite(rawWidth) || rawWidth < NAV_SNAP_COLLAPSE_WIDTH) {
    return { collapsed: true, width: clampNavWidth(prevWidth) };
  }
  return { collapsed: false, width: clampNavWidth(rawWidth) };
}

/** Effective rendered width for a state. */
export function effectiveNavWidth(state: NavState): number {
  return state.collapsed ? NAV_COLLAPSED_WIDTH : clampNavWidth(state.width);
}

/** Parse a stored JSON value; anything malformed falls back to defaults. */
export function parseNavState(raw: string | null | undefined): NavState {
  if (!raw) return { ...DEFAULT_NAV_STATE };
  try {
    const obj: unknown = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return { ...DEFAULT_NAV_STATE };
    const o = obj as Record<string, unknown>;
    return {
      collapsed: o.collapsed === true,
      width: typeof o.width === 'number' ? clampNavWidth(o.width) : NAV_DEFAULT_WIDTH,
    };
  } catch {
    return { ...DEFAULT_NAV_STATE };
  }
}

export function serializeNavState(state: NavState): string {
  return JSON.stringify({ collapsed: !!state.collapsed, width: clampNavWidth(state.width) });
}

export function loadNavState(): NavState {
  try {
    return parseNavState(window.localStorage.getItem(NAV_STORAGE_KEY));
  } catch {
    return { ...DEFAULT_NAV_STATE };
  }
}

export function saveNavState(state: NavState): void {
  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, serializeNavState(state));
  } catch {
    /* storage unavailable — state simply isn't remembered */
  }
}
